// components/WaveAnalysisGrid.tsx (formerly WaveExportPreview)
import React, { useState, useContext, useEffect, useRef } from 'react';
import './WaveExportPreview.css'; // Keep using same CSS for now
import { SimplifiedChampion } from '../../XXX/gameTypes';
import { RowMeta, ChampionConfig } from '../types/staticTypes';
import StaticChampionCard from './StaticChampionCard';
import StaticAddChampion, { StaticChampionAddData } from './StaticAddChampion';
import GameAssetsContext from '../../XXX/context/GameAssetContext';
import '../../XXX/GameSimulator/GameContainer2/GameGrid/GameGrid.css'; // Use exact same CSS

interface Props {
  response: WaveAnalysisResponse | null;
  loading: boolean;
  error: string | null;
  onExportToDamageCalculator?: (mainChampion: ChampionConfig, targetChampion: ChampionConfig) => void;
  onAddRow?: (rowIndex: number) => Promise<void>;
  onDuplicateRow?: (rowIndex: number) => Promise<void>;
  championName?: string;
  enablePolling?: boolean; // New prop to enable/disable polling
  onPollingUpdate?: (response: WaveAnalysisResponse) => void; // Callback for polling updates
  defaultLane?: string; // e.g. 'TOP', 'MIDDLE', 'BOTTOM', 'UTILITY', 'JUNGLE'
}

// Renamed from WaveExportResponse to WaveAnalysisResponse
export interface WaveAnalysisResponse {
    success: boolean;
    error?: string;
    data?: {
      champions: SimplifiedChampion[][];
      metadata: {
        mode: string;
        mainChampionName: string;
        lastUpdated: string;
        rows: RowMeta[];
        waves: number[];
        // legacy kept by backend; present but you can ignore:
        batchInfo?: any;
      };
    };
  }

// Level-up milestone waves per role category
// These are the waves where champions hit levels 1, 2, 3, and 6
const LEVEL_UP_WAVES: Record<string, number[]> = {
  solo: [1, 2, 3, 9],     // Mid, Top, Jungle — solo XP
  duo:  [1, 2, 4, 12],    // Bot, Support — shared XP
};

function getLaneType(lane?: string): 'solo' | 'duo' {
  if (!lane) return 'solo';
  const upper = lane.toUpperCase();
  return (upper === 'BOTTOM' || upper === 'UTILITY') ? 'duo' : 'solo';
}

// --- Wave range grouping helpers ---
// A segment is either a single wave or a contiguous range of collapsed waves
type WaveSegment =
  | { type: 'wave'; waveNumber: number }
  | { type: 'range'; waveNumbers: number[]; key: string };

/**
 * Groups consecutive collapsed waves into range segments for a compact display.
 * A run of 2+ collapsed waves becomes a single "range" segment (e.g. Waves 5-11).
 * Individual waves (expanded or a lone collapsed wave) stay as-is.
 */
function groupWavesIntoSegments(
  waveNumbers: number[],
  collapsedWaves: Record<number, boolean>
): WaveSegment[] {
  const segments: WaveSegment[] = [];
  let i = 0;

  while (i < waveNumbers.length) {
    if (collapsedWaves[waveNumbers[i]]) {
      // Accumulate consecutive collapsed waves
      const rangeStart = i;
      while (i < waveNumbers.length && collapsedWaves[waveNumbers[i]]) {
        i++;
      }
      const rangeWaves = waveNumbers.slice(rangeStart, i);

      if (rangeWaves.length >= 2) {
        segments.push({
          type: 'range',
          waveNumbers: rangeWaves,
          key: `${rangeWaves[0]}-${rangeWaves[rangeWaves.length - 1]}`,
        });
      } else {
        // Single collapsed wave – just show it normally
        segments.push({ type: 'wave', waveNumber: rangeWaves[0] });
      }
    } else {
      segments.push({ type: 'wave', waveNumber: waveNumbers[i] });
      i++;
    }
  }

  return segments;
}

const WaveAnalysisGrid: React.FC<Props> = ({ 
  response, 
  loading, 
  error, 
  onExportToDamageCalculator,
  onAddRow,
  onDuplicateRow,
  championName,
  enablePolling = false,
  onPollingUpdate,
  defaultLane
}) => {
  const [collapsedWaves, setCollapsedWaves] = useState<Record<number, boolean>>({});
  // Track which collapsed-wave ranges have been "unrolled" to show individual wave headers
  const [expandedRanges, setExpandedRanges] = useState<Record<string, boolean>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({
    'Early Game': false,           // Expanded by default
    'Early Laning Phase': false,   // Expanded by default
    'Item Powerspikes': false,     // Expanded by default - SHOW THE POWER SPIKES!
    'Mid Game Test': true,         // Collapsed by default
  });
  
  // State for champion selection
  const [selectedCell, setSelectedCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingError, setPollingError] = useState<string | null>(null);
  const [lastPollingUpdate, setLastPollingUpdate] = useState<Date | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [pollingComplete, setPollingComplete] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  
  // Local state to track the current response data so we can update individual champions
  const [localResponse, setLocalResponse] = useState<WaveAnalysisResponse | null>(response);
  const { gameWideAssets } = useContext(GameAssetsContext);
  const openInCalculatorImage = gameWideAssets.damageIcon;
  
  // Track previous response to detect changes and preserve scroll
  const prevResponseRef = useRef<WaveAnalysisResponse | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastDataHashRef = useRef<string | null>(null);
  // Track whether we've already auto-collapsed for this dataset
  const hasAutoCollapsedRef = useRef<string | null>(null);
  
  // Sync local response with prop response when it changes
  useEffect(() => {
    if (response !== localResponse) {
      setLocalResponse(response);
    }
  }, [response]);

  // ── Auto-collapse waves based on role and group type ──
  // Early Laning Phase: only level-up milestone waves are expanded
  // Item Powerspikes: all expanded (every wave is a final-item completion)
  useEffect(() => {
    const data = localResponse?.data;
    if (!data?.champions || !data.metadata?.rows) return;

    // Build a fingerprint so we only auto-collapse once per dataset
    const fingerprint = `${championName}-${defaultLane}-${data.metadata.rows.length}`;
    if (hasAutoCollapsedRef.current === fingerprint) return;
    hasAutoCollapsedRef.current = fingerprint;

    const laneType = getLaneType(defaultLane);
    const milestoneWaves = new Set(LEVEL_UP_WAVES[laneType]);

    const newCollapsed: Record<number, boolean> = {};
    const rows = data.metadata.rows;

    rows.forEach((meta) => {
      if (meta.kind !== 'wave') return;
      const waveNum = (meta as any).waveNumber as number;
      const groupLabel: string = (meta as any).groupLabel || '';

      if (groupLabel === 'Early Laning Phase') {
        // Collapse non-milestone waves
        if (!milestoneWaves.has(waveNum)) {
          newCollapsed[waveNum] = true;
        }
      }
      // Item Powerspikes: leave expanded (don't add to collapsed)
    });

    setCollapsedWaves(prev => ({ ...prev, ...newCollapsed }));
  }, [localResponse, championName, defaultLane]);
  
  // Clear local state when championName changes to prevent showing stale data
  useEffect(() => {
    console.log(`[WaveAnalysisGrid] 🔄 Champion changed to: ${championName}, clearing local state`);
    setLocalResponse(null);
    setSelectedCell(null);
    setPollingComplete(false);
    setLoadingProgress(null);
    setPollingError(null);
    setLastPollingUpdate(null);
    setCollapsedWaves({}); // Reset wave collapse state
    setExpandedRanges({}); // Reset range expansion state
    hasAutoCollapsedRef.current = null; // Allow auto-collapse for new champion
    
    // Clear polling interval if it exists
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, [championName]);
  
  // Helper function to merge new grid data while preserving locally updated champions
  const mergeGridData = (newChampions: SimplifiedChampion[][], newMetadata: any): WaveAnalysisResponse => {
    if (!localResponse?.data?.champions) {
      // If no local data, just use the new data
      return {
        success: true,
        data: {
          champions: newChampions,
          metadata: newMetadata
        }
      };
    }
    
    // Create a map of existing champions by ID for quick lookup
    const existingChampionsMap = new Map<string, SimplifiedChampion>();
    localResponse.data.champions.forEach(row => {
      row.forEach(champion => {
        if (champion?.id) {
          existingChampionsMap.set(champion.id, champion);
        }
      });
    });
    
    // Merge new champions with existing ones, preserving local updates
    const mergedChampions = newChampions.map(row => 
      row.map(newChampion => {
        if (newChampion?.id && existingChampionsMap.has(newChampion.id)) {
          // Use the locally updated version if it exists
          const existingChampion = existingChampionsMap.get(newChampion.id)!;
          console.log(`[WaveAnalysisGrid] Preserving locally updated champion: ${existingChampion.name} (${existingChampion.id})`);
          return existingChampion;
        }
        // Use the new champion data for new or unchanged champions
        return newChampion;
      })
    );
    
    return {
      success: true,
      data: {
        champions: mergedChampions,
        metadata: newMetadata
      }
    };
  };

  // Handler for when individual champions are updated
  const handleChampionUpdate = (updatedChampion: SimplifiedChampion) => {
    console.log('[WaveAnalysisGrid] Handling champion update:', updatedChampion.id, updatedChampion.name);
    
    if (!localResponse?.data?.champions) {
      console.warn('[WaveAnalysisGrid] No local response data to update');
      return;
    }
    
    // Find and update the champion in the local response data
    const updatedChampions = localResponse.data.champions.map((row, rowIndex) => 
      row.map((champion, colIndex) => {
        if (champion && champion.id === updatedChampion.id) {
          console.log(`[WaveAnalysisGrid] Found champion to update at row ${rowIndex}, col ${colIndex}`);
          console.log('[WaveAnalysisGrid] Old champion runes:', champion.runePage?.shards);
          console.log('[WaveAnalysisGrid] New champion runes:', updatedChampion.runePage?.shards);
          return updatedChampion;
        }
        return champion;
      })
    );
    
    // Update the local response state
    const updatedResponse: WaveAnalysisResponse = {
      ...localResponse,
      data: {
        ...localResponse.data,
        champions: updatedChampions
      }
    };
    
    setLocalResponse(updatedResponse);
    console.log('[WaveAnalysisGrid] Local response updated with new champion data');
  };
  
  // Polling function to check for champion grid updates
  const pollForChampionUpdates = async () => {
    try {
      setIsPolling(true);
      setPollingError(null);
      
      const fetchResponse = await fetch('/api/static/grid/status');
      if (!fetchResponse.ok) {
        throw new Error(`HTTP error! status: ${fetchResponse.status}`);
      }
      
      const result = await fetchResponse.json();
      if (result.success && result.data) {
        console.log('[WaveAnalysisGrid] Polling update:', {
          totalRows: result.data.totalRows,
          totalChampions: result.data.totalChampions,
          hasAllDefaults: result.data.hasAllDefaults,
          loadedWaves: result.data.loadedWaves
        });
        
        // Convert backend response to WaveAnalysisResponse format
        const analysisResponse: WaveAnalysisResponse = {
          success: true,
          data: {
            champions: result.data.champions,
            metadata: {
              ...result.data.metadata,
              waveGroupStatus: result.data.waveGroupStatus // Add wave group status
            }
          }
        };
        
        setLastPollingUpdate(new Date());
        
        // Update loading progress using actual wave group status from backend
        if (result.data.waveGroupStatus) {
          const waveGroups = result.data.waveGroupStatus;
          
          // Check if all groups are complete (regardless of wave count)
          const allGroupsComplete = waveGroups.every((group: any) => group.isComplete);
          
          // Calculate total expected and loaded waves from all groups
          const totalExpectedWaves = waveGroups.reduce((total: number, group: any) => {
            if (group.expectedWaves) {
              return total + group.expectedWaves.length;
            }
            return total;
          }, 0);
          
          const totalLoadedWaves = waveGroups.reduce((total: number, group: any) => {
            if (group.loadedWaves) {
              return total + group.loadedWaves.length;
            }
            return total;
          }, 0);
          
          console.log(`[WaveAnalysisGrid] 📊 Progress calculation:`, {
            totalExpectedWaves,
            totalLoadedWaves,
            allGroupsComplete,
            waveGroups: waveGroups.map((g: any) => ({
              name: g.name,
              expected: g.expectedWaves?.length || 0,
              loaded: g.loadedWaves?.length || 0,
              isComplete: g.isComplete,
              isDynamic: g.isDynamic
            }))
          });
          
          // If all groups are complete, stop showing loading progress
          if (allGroupsComplete) {
            setLoadingProgress(null);
            setPollingComplete(true);
          } else {
            // Just show loading indicator without specific numbers
            setLoadingProgress({ loaded: 0, total: 1 });
          }
        } else {
          // Fallback: Just show loading indicator if waveGroupStatus is not available
          if (result.data.hasAllDefaults) {
            setLoadingProgress(null);
            setPollingComplete(true);
          } else {
            setLoadingProgress({ loaded: 0, total: 1 });
          }
        }
        
        console.log(`[WaveAnalysisGrid] 📊 Polling update:`, {
          totalRows: result.data.totalRows,
          totalChampions: result.data.totalChampions,
          loadedWaves: result.data.loadedWaves,
          hasAllDefaults: result.data.hasAllDefaults,
          championsGrid: result.data.champions?.length || 0,
          waveGroupStatus: result.data.waveGroupStatus
        });
        
        // Only notify parent if data actually changed AND we don't already have champions displayed
        // This prevents polling from overriding the main response when champions are already loaded
        const currentDataHash = JSON.stringify(analysisResponse.data?.champions);
        const hasExistingChampions = (response?.data?.champions?.length || 0) > 0;
        
        if (onPollingUpdate && lastDataHashRef.current !== currentDataHash && !hasExistingChampions) {
          console.log('[WaveAnalysisGrid] Data changed and no existing champions, notifying parent');
          lastDataHashRef.current = currentDataHash;
          onPollingUpdate(analysisResponse);
        } else if (hasExistingChampions) {
          console.log('[WaveAnalysisGrid] Skipping parent update - champions already displayed from main response');
        }
        
        // Stop polling if all wave groups are complete
        const shouldStopPolling = result.data.waveGroupStatus ? 
          result.data.waveGroupStatus.every((group: any) => group.isComplete) :
          result.data.hasAllDefaults; // Fallback to legacy logic
          
        if (shouldStopPolling) {
          console.log('[WaveAnalysisGrid] All wave groups complete, stopping polling');
          setIsPolling(false);
          setLoadingProgress(null); // Clear progress when complete
          setPollingComplete(true); // Mark polling as complete to prevent restart
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } else {
        setPollingError(result.error || 'Failed to check champion grid status');
      }
    } catch (error) {
      console.error('[WaveAnalysisGrid] Polling error:', error);
      setPollingError(error instanceof Error ? error.message : 'Unknown polling error');
    } finally {
      setIsPolling(false);
    }
  };

  // Polling effect
  useEffect(() => {
    if (enablePolling && !pollingComplete) {
      console.log('[WaveAnalysisGrid] Starting polling for champion updates...');
      
      // Initial poll
      pollForChampionUpdates();
      
      // Set up interval polling every 5 seconds
      pollingIntervalRef.current = setInterval(pollForChampionUpdates, 5000);
      
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    } else if (pollingComplete) {
      console.log('[WaveAnalysisGrid] Polling already complete, skipping setup');
    }
  }, [enablePolling, pollingComplete]); // Removed onPollingUpdate dependency to prevent restarts

  useEffect(() => {
    // If response changed (new export), preserve scroll position
    if (response && prevResponseRef.current !== response) {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      // Multiple attempts to restore scroll position as content loads
      const restoreScroll = () => {
        window.scrollTo(scrollX, scrollY);
      };
      
      // Immediate restore
      restoreScroll();
      
      // Delayed restores to handle async content loading
      setTimeout(restoreScroll, 0);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 150);
      
      prevResponseRef.current = response;
    }
  }, [response]);
  
  // Toggle wave collapse state
  const toggleWave = (waveNumber: number) => {
    setCollapsedWaves(prev => ({
      ...prev,
      [waveNumber]: !prev[waveNumber]
    }));
  };

  // Handle cell clicks for champion selection
  const handleCellClick = (rowIndex: number, colIndex: number) => {
    // Only allow selection of empty cells
    if (localResponse?.data?.champions?.[rowIndex]?.[colIndex]) {
      return; // Cell is occupied
    }

    // Toggle selection
    if (selectedCell?.rowIndex === rowIndex && selectedCell?.colIndex === colIndex) {
      setSelectedCell(null); // Deselect if clicking the same cell
    } else {
      setSelectedCell({ rowIndex, colIndex }); // Select this cell
    }
  };

  // Handle champion selection from AddChampion component
  const handleChampionSelected = async (championData: StaticChampionAddData) => {
    if (!selectedCell) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/champion/add-to-position`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName: championData.name,
          level: championData.level,
          team: championData.team,
          rowIndex: selectedCell.rowIndex,
          colIndex: selectedCell.colIndex
        }),
      });

      const result = await response.json();

      if (result.success) {
        console.log('[handleChampionSelected] Champion added successfully');
        console.log('[handleChampionSelected] Backend returned:', {
          champions: result.data.champions,
          metadata: result.data.metadata,
          rowMeta: result.data.metadata?.rows,
          championCount: result.data.champions?.length || 0,
          newChampionAtPosition: result.data.champions?.[selectedCell.rowIndex]?.[selectedCell.colIndex]
        });
        
        // Update local state with the new grid data
        const mergedResponse = mergeGridData(result.data.champions, result.data.metadata);
        console.log('[handleChampionSelected] Merged response:', {
          champions: mergedResponse.data?.champions,
          championCount: mergedResponse.data?.champions?.length || 0,
          newChampionAtPosition: mergedResponse.data?.champions?.[selectedCell.rowIndex]?.[selectedCell.colIndex]
        });
        setLocalResponse(mergedResponse);
        
        // Clear selection
        setSelectedCell(null);
      } else {
        console.error('[handleChampionSelected] Failed to add champion:', result.error);
        alert(`Failed to add champion: ${result.error}`);
      }
    } catch (error) {
      console.error('[handleChampionSelected] Error adding champion:', error);
      alert('Failed to add champion. Please try again.');
    }
  };

  // Handle canceling champion selection
  const handleCancelSelection = () => {
    setSelectedCell(null);
  };

  // Toggle group collapse state
  const toggleGroup = (groupLabel: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel]
    }));
  };

  // Handle export to damage calculator
  const handleExportToDamageCalculator = (rowIndex: number) => {
    if (!localResponse?.data?.champions || !onExportToDamageCalculator) return;

    const row = localResponse.data.champions[rowIndex];
    if (!row || row.length < 2) return;

    const mainChampion = row[0];
    const targetChampion = row[1];

    const extractChampionConfig = (champion: SimplifiedChampion): ChampionConfig => {
      return {
        name: champion.name,
        level: champion.level,
        team: champion.team as 'blue' | 'red',
        items: champion.inventory?.items?.map(item => item.id) || [],
        runes: champion.runePage ? {
          primaryPath: champion.runePage.primaryPath,
          primaryRunes: champion.runePage.primaryRunes,
          secondaryPath: champion.runePage.secondaryPath,
          secondaryRunes: champion.runePage.secondaryRunes,
          shards: champion.runePage.shards
        } : null,
        skillOrder: champion.currentSkillOrder || []
      };
    };

    const mainConfig = extractChampionConfig(mainChampion);
    const targetConfig = extractChampionConfig(targetChampion);

    onExportToDamageCalculator(mainConfig, targetConfig);
  };

  // Handle wave-specific row operations
  const handleAddWaveRow = async (waveNumber: number) => {
    if (!championName) return;
    
    try {
      const response = await fetch('/api/static/wave/add-row', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName: championName,
          afterWaveNumber: waveNumber
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.success && result.data) {
        console.log('Successfully added wave row');
        // Merge new grid data while preserving locally updated champions
        const mergedResponse = mergeGridData(result.data.champions, result.data.metadata);
        setLocalResponse(mergedResponse);
        console.log('[WaveAnalysisGrid] Merged grid data after adding row, preserving local champion updates');
      } else {
        console.error('Failed to add wave row:', result.error);
      }
    } catch (error) {
      console.error('Error adding wave row:', error);
    }
  };

  const handleDuplicateWaveRow = async (waveNumber: number) => {
    if (!championName) return;
    
    try {
      const response = await fetch('/api/static/wave/duplicate-row', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName: championName,
          waveNumber: waveNumber
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.success && result.data) {
        console.log('Successfully duplicated wave row');
        // Merge new grid data while preserving locally updated champions
        const mergedResponse = mergeGridData(result.data.champions, result.data.metadata);
        setLocalResponse(mergedResponse);
        console.log('[WaveAnalysisGrid] Merged grid data after duplicating row, preserving local champion updates');
      } else {
        console.error('Failed to duplicate wave row:', result.error);
      }
    } catch (error) {
      console.error('Error duplicating wave row:', error);
    }
  };

  // Render row separators with add button
  const renderRowSeparator = (rowIndex: number, waveNumber: number) => (
    <div className="row-separator" key={`separator-${rowIndex}`}>
      <div className="row-add-buttons">
        <button 
          className="row-add-button" 
          onClick={() => handleAddWaveRow(waveNumber)}
          title="Add new wave row"
        >
          +
        </button>
        <button 
          className="row-add-button duplicate-button" 
          onClick={() => handleDuplicateWaveRow(waveNumber)}
          title="Duplicate this wave"
        >
          ⎘
        </button>
        <button 
          className="row-add-button export-button" 
          onClick={() => handleExportToDamageCalculator(rowIndex)}
          title="Export to Damage Calculator"
          disabled={!onExportToDamageCalculator}
        >
          <img src={openInCalculatorImage} alt="Open in Calculator" />
        </button>
      </div>
    </div>
  );

  // Helper function to group rows by wave groups using metadata
  const groupRowsByWaveGroups = (champions: SimplifiedChampion[][], rows: RowMeta[]) => {
    // First collect all wave entries
    const waveEntries: Array<{
      waveNumber: number;
      championRow: SimplifiedChampion[];
      rowIndex: number;
      groupLabel?: string;
    }> = [];

    rows.forEach((meta, index) => {
      if (meta.kind === 'wave' && champions[index]) {
        waveEntries.push({
          waveNumber: meta.waveNumber,
          championRow: champions[index],
          rowIndex: index,
          groupLabel: meta.groupLabel
        });
      }
    });

    // Group by groupLabel, then sort by wave number within groups
    const groups = new Map<string, typeof waveEntries>();
    
    waveEntries.forEach(entry => {
      const groupKey = entry.groupLabel || 'Ungrouped';
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(entry);
    });

    // Convert to array and sort groups, and waves within groups
    return Array.from(groups.entries()).map(([groupLabel, waves]) => ({
      groupLabel,
      waves: waves.sort((a, b) => a.waveNumber - b.waveNumber)
    }));
  };

  return (
    <div className="wave-export-preview">
      <h4
        onClick={() => setShowDescription(!showDescription)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title="Click for more info"
      >
        Champion Analysis Grid
        <span 
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 18, 
            height: 18, 
            borderRadius: '50%', 
            border: '1.5px solid var(--text-secondary, #888)', 
            fontSize: 12, 
            fontWeight: 600,
            fontStyle: 'italic',
            fontFamily: 'serif',
            color: showDescription ? 'var(--text-accent, #fff)' : 'var(--text-secondary, #888)', 
            marginLeft: 8,
            verticalAlign: 'middle',
            lineHeight: 1,
            opacity: showDescription ? 1 : 0.7,
            transition: 'opacity 0.2s, color 0.2s'
          }}
        >
          i
        </span>
      </h4>
      
      {/* Description Section - hidden by default, toggle by clicking the heading */}
      {showDescription && (
        <div className="wave-analysis-description">
          <div className="wave-analysis-description-content">
            <p>
             This section provides detailed comparison of damage output and combat performance between two champions. 
              Each champion card displays comprehensive combo sequences and damage values for abilities. Click on any combo to expand and view detailed breakdowns of each action - 
              damage calculations, passive ability triggers, stat modifications, and crowd control applications.
            </p>
            <p className="secondary-description">
              You can fully customize each champion by adjusting runes, items, and skill order to see how changes affect performance. 
              Hover at the bottom of each wave pair to add another row, copy the existing setup for build comparisons, or export directly to the damage calculator sandbox for custom combo testing.
            </p>
          </div>
        </div>
      )}

      {loading && (
        <div className="wave-export-loading">
          <div className="simple-spinner small" />
          <span>Exporting waves…</span>
        </div>
      )}

      {error && (
        <div className="wave-export-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {(!localResponse || loadingProgress) && !error && (
        <div className="loading-message">
          <div className="loading-spinner"></div>
          <span>{loadingProgress ? 'Loading champion waves...' : 'Waiting for champion analysis...'}</span>
        </div>
      )}

      {!loading && !error && localResponse && localResponse.data && !loadingProgress && (
        <div className="wave-export-result">
          
          {/* Simplified Champion Grid Display - Show All Champions First */}
          <div className="wave-export-champions">
            {(() => {
              const rowMeta = localResponse.data.metadata.rows;
              
              // SIMPLIFIED APPROACH: Show all champions first, group by metadata after
              console.log('[WaveAnalysisGrid] 🔍 Debug - Full response structure:', {
                hasResponse: !!localResponse,
                hasData: !!localResponse.data,
                hasChampions: !!localResponse.data.champions,
                hasMetadata: !!localResponse.data.metadata,
                metadataKeys: localResponse.data.metadata ? Object.keys(localResponse.data.metadata) : [],
                fullMetadata: localResponse.data.metadata
              });
              
              console.log('[WaveAnalysisGrid] 🔍 Debug - Champions data:', {
                totalChampions: localResponse.data.champions?.length || 0,
                totalRowMeta: rowMeta?.length || 0,
                championSample: localResponse.data.champions?.[0]?.map(c => c?.name) || [],
                rowMetaSample: rowMeta?.slice(0, 3).map(r => ({ kind: r.kind, waveNumber: (r as any).waveNumber, groupLabel: (r as any).groupLabel })) || []
              });
              
              // Create wave entries for all champions
              const allWaveEntries: Array<{
                waveNumber: number;
                championRow: SimplifiedChampion[];
                rowIndex: number;
                groupLabel: string;
              }> = [];

              localResponse.data.champions.forEach((championRow, rowIndex) => {
                // Safety check for rowMeta existence
                if (!rowMeta || rowIndex >= rowMeta.length) {
                  console.warn(`[WaveAnalysisGrid] Missing rowMeta for row ${rowIndex}, skipping`);
                  return;
                }
                
                const meta = rowMeta[rowIndex];
                if (meta && meta.kind === 'wave') {
                  const waveMeta = meta as any; // Cast to access waveNumber and groupLabel
                  allWaveEntries.push({
                    waveNumber: waveMeta.waveNumber,
                    championRow,
                    rowIndex,
                    groupLabel: waveMeta.groupLabel || 'Ungrouped'
                  });
                }
              });

              console.log('[WaveAnalysisGrid] 🔍 All wave entries:', allWaveEntries.map(e => ({ wave: e.waveNumber, group: e.groupLabel })));
              console.log('[WaveAnalysisGrid] 🔍 Champions vs RowMeta alignment check:');
              console.log('  - Champions array length:', localResponse.data.champions.length);
              console.log('  - RowMeta array length:', rowMeta?.length || 0);
              console.log('  - RowMeta contents:', rowMeta?.map((meta, idx) => ({ 
                index: idx, 
                kind: meta?.kind, 
                waveNumber: (meta as any)?.waveNumber,
                groupLabel: (meta as any)?.groupLabel 
              })));

              // Group by groupLabel
              const groupedEntries = new Map<string, typeof allWaveEntries>();
              allWaveEntries.forEach(entry => {
                if (!groupedEntries.has(entry.groupLabel)) {
                  groupedEntries.set(entry.groupLabel, []);
                }
                groupedEntries.get(entry.groupLabel)!.push(entry);
              });

              // Sort groups and waves within groups
              const sortedGroups = Array.from(groupedEntries.entries()).map(([groupLabel, waves]) => ({
                groupLabel,
                waves: waves.sort((a, b) => a.waveNumber - b.waveNumber)
              }));

              console.log('[WaveAnalysisGrid] 🔍 Grouped entries:', sortedGroups.map(g => ({ group: g.groupLabel, waves: g.waves.map(w => w.waveNumber) })));
              console.log('[WaveAnalysisGrid] 🚨 Will show fallback?', sortedGroups.length === 0 && localResponse.data.champions.length > 0);

              // If no grouped entries, show a fallback display of all champions
              if (sortedGroups.length === 0 && localResponse.data.champions.length > 0) {
                console.log('[WaveAnalysisGrid] 🚨 No grouped entries found, showing fallback display');
                return (
                  <div className="wave-group-section">
                    <div className="wave-group-header">
                      <div className="wave-group-title-section">
                        <h5>All Champions (Fallback Display)</h5>
                        <span className="wave-group-waves">- {localResponse.data.champions.length} rows</span>
                      </div>
                    </div>
                    <div className="fallback-champions-grid">
                      {localResponse.data.champions.map((championRow, rowIndex) => (
                        <div key={`fallback-row-${rowIndex}`} className="wave-section">
                          <div className="wave-section-header">
                            <span className="wave-title">Row {rowIndex + 1}</span>
                          </div>
                          <div className="champion-row">
                            {championRow.map((champion, colIndex) => (
                              champion ? (
                                <div key={`${rowIndex}-${colIndex}`} className="champion-card">
                                  <div className="champion-info">
                                    <span className="champion-name">{champion.name}</span>
                                    <span className="champion-level">Level {champion.level}</span>
                                    <span className="champion-team">{champion.team}</span>
                                  </div>
                                </div>
                              ) : (
                                <div key={`${rowIndex}-${colIndex}`} className="champion-card empty">
                                  Empty
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              return sortedGroups.map(({ groupLabel, waves }) => {
                const isGroupCollapsed = collapsedGroups[groupLabel];
                const uniqueWaveNumbers = [...new Set(waves.map(w => w.waveNumber))].sort((a, b) => a - b);
                
                return (
                  <div key={`group-${groupLabel}`} className="wave-group-section">
                    <div 
                      className="wave-group-header clickable"
                      onClick={() => toggleGroup(groupLabel)}
                    >
                      <div className="wave-group-title-section">
                        <span className={`wave-group-arrow ${!isGroupCollapsed ? 'expanded' : ''}`}>▶</span>
                        <h5>{groupLabel}</h5>
                        <span className="wave-group-waves">- Waves {uniqueWaveNumbers.join(', ')}</span>
                      </div>
                      <span className="wave-group-count">{uniqueWaveNumbers.length} wave{uniqueWaveNumbers.length === 1 ? '' : 's'}</span>
                    </div>
                  
                    {!isGroupCollapsed && (() => {
                      // Group consecutive collapsed waves into compact range rows
                      const segments = groupWavesIntoSegments(uniqueWaveNumbers, collapsedWaves);

                      return segments.map((segment) => {
                        // ── Range segment: a compact summary for 2+ consecutive collapsed waves ──
                        if (segment.type === 'range') {
                          const { waveNumbers: rangeWaves, key: rangeKey } = segment;
                          const first = rangeWaves[0];
                          const last = rangeWaves[rangeWaves.length - 1];
                          const isRangeExpanded = !!expandedRanges[rangeKey];

                          // Compute timing span for the range
                          const firstMeta = rowMeta?.find((m: RowMeta) => m.kind === 'wave' && (m as any).waveNumber === first);
                          const lastMeta  = rowMeta?.find((m: RowMeta) => m.kind === 'wave' && (m as any).waveNumber === last);
                          const rangeStart = firstMeta && (firstMeta as any).arrivalTime;
                          const rangeEnd   = lastMeta  && (lastMeta  as any).clearTime;

                          return (
                            <div key={`range-${rangeKey}`} className="wave-range-section">
                              {/* Range summary header */}
                              <div
                                className="wave-range-header"
                                onClick={() => setExpandedRanges(prev => ({ ...prev, [rangeKey]: !prev[rangeKey] }))}
                              >
                                <div className="wave-header-content">
                                  <span className="wave-range-toggle">{isRangeExpanded ? '▼' : '▶'}</span>
                                  <span className="wave-title">Waves {first}–{last}</span>
                                  {rangeStart && rangeEnd && (
                                    <span className="wave-timing-inline">{rangeStart} → {rangeEnd}</span>
                                  )}
                                  <span className="wave-row-count">({rangeWaves.length} waves)</span>
                                </div>
                              </div>

                              {/* When expanded, show each wave individually (still collapsed) */}
                              {isRangeExpanded && rangeWaves.map((waveNumber) => {
                                const waveRows = waves.filter(w => w.waveNumber === waveNumber);
                                return (
                                  <div key={`wave-${waveNumber}`} className="wave-section wave-section-in-range">
                                    <div
                                      className="wave-section-header"
                                      onClick={() => toggleWave(waveNumber)}
                                    >
                                      <div className="wave-header-content">
                                        <span className="wave-title">Wave {waveNumber}</span>
                                        {rowMeta && (() => {
                                          const waveMeta = rowMeta.find((meta: RowMeta) => meta.kind === 'wave' && (meta as any).waveNumber === waveNumber);
                                          if (waveMeta && waveMeta.kind === 'wave' && ((waveMeta as any).arrivalTime || (waveMeta as any).clearTime)) {
                                            return <span className="wave-timing-inline">{(waveMeta as any).arrivalTime} → {(waveMeta as any).clearTime}</span>;
                                          }
                                          return null;
                                        })()}
                                        <span className="wave-row-count">({waveRows.length} row{waveRows.length === 1 ? '' : 's'})</span>
                                      </div>
                                      <span className="wave-section-toggle">
                                        {collapsedWaves[waveNumber] ? '▶' : '▼'}
                                      </span>
                                    </div>

                                    {!collapsedWaves[waveNumber] && (
                                      <div className="wave-section-content">
                                        {waveRows.map(({ championRow, rowIndex }, waveRowIndex) => (
                                          <div key={`wave-${waveNumber}-row-${waveRowIndex}`} className="wave-champion-row">
                                            <div className="grid-row">
                                              {championRow.map((champion: SimplifiedChampion, colIndex: number) => {
                                                const teamClass = colIndex === 0 ? 'team-blue' : 'team-red';
                                                const targetColIndex = colIndex === 0 ? 1 : 0;
                                                const targetChampion = championRow[targetColIndex] || null;
                                                const isSelectedCell = selectedCell?.rowIndex === rowIndex && selectedCell?.colIndex === colIndex;
                                                const cellClasses = ['grid-cell', teamClass, isSelectedCell && !champion ? 'selected-cell' : ''].filter(Boolean).join(' ');

                                                return (
                                                  <div
                                                    key={`wave-${waveNumber}-row-${waveRowIndex}-${champion?.id || `empty-${colIndex}`}`}
                                                    className={cellClasses}
                                                    onClick={() => !champion && handleCellClick(rowIndex, colIndex)}
                                                    style={{ cursor: !champion ? 'pointer' : 'default' }}
                                                  >
                                                    {isSelectedCell && !champion ? (
                                                      <StaticAddChampion onSelectChampion={handleChampionSelected} onCancel={handleCancelSelection} defaultTeam={colIndex === 0 ? 'blue' : 'red'} />
                                                    ) : champion ? (
                                                      <StaticChampionCard key={champion.id} champion={champion} regularLogs={[]} comboLogs={[]} targetChampion={targetChampion} onSelectTargetClick={() => {}} onChampionUpdate={handleChampionUpdate} />
                                                    ) : (
                                                      <div className='empty-cell'>Click to add champion</div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            {renderRowSeparator(rowIndex, waveNumber)}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }

                        // ── Single wave segment (expanded or lone collapsed wave) ──
                        const waveNumber = segment.waveNumber;
                        const waveRows = waves.filter(w => w.waveNumber === waveNumber);

                        return (
                          <div key={`wave-${waveNumber}`} className="wave-section">
                            <div
                              className="wave-section-header"
                              onClick={() => toggleWave(waveNumber)}
                            >
                              <div className="wave-header-content">
                                <span className="wave-title">Wave {waveNumber}</span>
                                {rowMeta && (() => {
                                  const waveMeta = rowMeta.find((meta: RowMeta) => meta.kind === 'wave' && (meta as any).waveNumber === waveNumber);
                                  if (waveMeta && waveMeta.kind === 'wave' && ((waveMeta as any).arrivalTime || (waveMeta as any).clearTime)) {
                                    return <span className="wave-timing-inline">{(waveMeta as any).arrivalTime} → {(waveMeta as any).clearTime}</span>;
                                  }
                                  return null;
                                })()}
                                <span className="wave-row-count">({waveRows.length} row{waveRows.length === 1 ? '' : 's'})</span>
                              </div>
                              <span className="wave-section-toggle">
                                {collapsedWaves[waveNumber] ? '▶' : '▼'}
                              </span>
                            </div>

                            {!collapsedWaves[waveNumber] && (
                              <div className="wave-section-content">
                                {waveRows.map(({ championRow, rowIndex }, waveRowIndex) => (
                                  <div key={`wave-${waveNumber}-row-${waveRowIndex}`} className="wave-champion-row">
                                    <div className="grid-row">
                                      {championRow.map((champion: SimplifiedChampion, colIndex: number) => {
                                        const teamClass = colIndex === 0 ? 'team-blue' : 'team-red';
                                        const targetColIndex = colIndex === 0 ? 1 : 0;
                                        const targetChampion = championRow[targetColIndex] || null;
                                        const isSelectedCell = selectedCell?.rowIndex === rowIndex && selectedCell?.colIndex === colIndex;
                                        const cellClasses = ['grid-cell', teamClass, isSelectedCell && !champion ? 'selected-cell' : ''].filter(Boolean).join(' ');

                                        return (
                                          <div
                                            key={`wave-${waveNumber}-row-${waveRowIndex}-${champion?.id || `empty-${colIndex}`}`}
                                            className={cellClasses}
                                            onClick={() => !champion && handleCellClick(rowIndex, colIndex)}
                                            style={{ cursor: !champion ? 'pointer' : 'default' }}
                                          >
                                            {isSelectedCell && !champion ? (
                                              <StaticAddChampion onSelectChampion={handleChampionSelected} onCancel={handleCancelSelection} defaultTeam={colIndex === 0 ? 'blue' : 'red'} />
                                            ) : champion ? (
                                              <StaticChampionCard key={champion.id} champion={champion} regularLogs={[]} comboLogs={[]} targetChampion={targetChampion} onSelectTargetClick={() => {}} onChampionUpdate={handleChampionUpdate} />
                                            ) : (
                                              <div className='empty-cell'>Click to add champion</div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    {renderRowSeparator(rowIndex, waveNumber)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                );
              });
            })()}
          </div>

          {/* Debug Info (Collapsible) */}
         {/*  <details className="wave-export-debug">
            <summary>Debug Info</summary>
            <pre>{JSON.stringify(localResponse, null, 2)}</pre>
          </details> */}
        </div>
      )}

      {!loading && !error && localResponse && !localResponse.data && localResponse.error && (
        <div className="wave-export-error">
          <pre>{localResponse.error}</pre>
        </div>
      )}
    </div>
  );
};

export default WaveAnalysisGrid;
